import { useMemo } from "react";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import { computeMomentum } from "./computeMomentum";
import { computeRecoveryTime } from "./computeRecoveryTime";
import type {
  MomentumOptions,
  MomentumResult,
  RecoveryOptions,
  RecoveryResult,
} from "./momentumTypes";

/**
 * EPIC-119 D1: content key over every field the momentum / recovery maths
 * reads. The old `${length}:${last.timestamp}` key stayed equal when a card
 * was deleted and re-logged for the other team at an earlier index, so the
 * memo kept the deleted card. Any add, remove or in-place edit (team, type,
 * card type, clock, period, timestamp, locations) now changes the key.
 */
function eventsKey(events: MatchEvent[]): string {
  let key = `${events.length}`;
  for (const e of events) {
    const d = (e.data ?? {}) as Record<string, unknown>;
    key += `|${e._id ?? e.client_id ?? ""},${e.team_id},${e.type},${
      d.card_type ?? ""
    },${e.period},${e.match_clock},${e.timestamp},${
      e.location ? e.location.join(":") : ""
    },${Array.isArray(d.end_location) ? d.end_location.join(":") : ""}`;
  }
  return key;
}

export function useMatchMomentum(
  events: MatchEvent[],
  opts: MomentumOptions,
): MomentumResult {
  const key = eventsKey(events);
  return useMemo(
    () => computeMomentum(events, opts),
    [key, opts.homeTeamId, opts.awayTeamId],
  );
}

export function useBallRecoveryTime(
  events: MatchEvent[],
  opts: RecoveryOptions,
): RecoveryResult {
  const key = eventsKey(events);
  return useMemo(
    () => computeRecoveryTime(events, opts),
    [key, opts.homeTeamId, opts.awayTeamId],
  );
}
